import * as React from "react";
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  type ToastProps,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/primitives/Toast";

interface ToastContextValue {
  toast: (options: {
    title?: string;
    description?: string;
    variant?: ToastProps["variant"];
    action?: {
      label: string;
      onClick: () => void;
    };
    duration?: number;
  }) => void;
  dismiss: (toastId?: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(
  undefined,
);

export interface ToastOptions {
  title?: string;
  description?: string;
  variant?: ToastProps["variant"];
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  id?: string;
}

interface ToastItem extends ToastOptions {
  id: string;
  visible: boolean;
}

export interface ToastContextProviderProps {
  children: React.ReactNode;
  swipeDirection?: "right" | "left" | "up" | "down";
  swipeThreshold?: number;
}

export function ToastContextProvider({
  children,
  swipeDirection = "right",
  swipeThreshold = 50,
}: ToastContextProviderProps) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const dismiss = React.useCallback((toastId?: string) => {
    setToasts((prev) => {
      if (toastId) {
        return prev.map((toast) =>
          toast.id === toastId ? { ...toast, visible: false } : toast,
        );
      }
      // If no ID is provided, dismiss all toasts
      return prev.map((toast) => ({ ...toast, visible: false }));
    });

    // Remove the toast after animation (700ms)
    setTimeout(() => {
      setToasts((prev) => {
        if (toastId) {
          return prev.filter((toast) => toast.id !== toastId);
        }
        return [];
      });
    }, 700);
  }, []);

  const toast = React.useCallback(
    (options: ToastOptions) => {
      const id = options.id || Math.random().toString(36).substring(2, 9);
      const duration = options.duration || 5000;

      // Add the toast
      setToasts((prev) => [...prev, { ...options, id, visible: true }]);

      // Auto-dismiss after duration
      if (duration !== Number.POSITIVE_INFINITY) {
        setTimeout(() => {
          dismiss(id);
        }, duration);
      }
    },
    [dismiss],
  );

  const contextValue = React.useMemo(
    () => ({
      dismiss,
      toast,
    }),
    [toast, dismiss],
  );

  return (
    <ToastContext value={contextValue}>
      <ToastProvider
        swipeDirection={swipeDirection}
        swipeThreshold={swipeThreshold}
      >
        {children}
        {toasts.map(({ id, title, description, variant, action, visible }) => (
          <Toast
            className={!visible ? "data-[state=closed]:animate-out" : ""}
            key={id}
            onOpenChange={(open) => {
              if (!open) dismiss(id);
            }}
            variant={variant}
          >
            {title && <ToastTitle>{title}</ToastTitle>}
            {description && <ToastDescription>{description}</ToastDescription>}
            {action && (
              <ToastAction
                altText={action.label}
                asChild
                onClick={action.onClick}
              >
                <button type="button">{action.label}</button>
              </ToastAction>
            )}
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </ToastContext>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastContextProvider");
  }
  return context;
}
